import { Role, WorkspaceRole, type AuthPrincipal } from '@mg/shared';
import { useAuth } from './AuthContext';

/**
 * Effective admin rights for the signed-in user in the ACTIVE workspace, per the roles
 * overhaul (docs/roles-approval-overhaul-design.md). Replaces the legacy permissive
 * `roles.length === 0 || roles.includes(Role.TENANT_ADMIN)` gate.
 *
 * `useIsWorkspaceAdmin()` — full read/edit of the workspace (Edit Brand Workspace,
 * members, approver matrix): an install-wide Tenant Admin (acts as Workspace Admin on
 * every workspace) OR the active membership's role is WORKSPACE_ADMIN. In dev-auth,
 * where the principal has no membership rows, it returns true so local work isn't
 * blocked — the API is the authoritative gate regardless.
 */
export function useIsWorkspaceAdmin(): boolean {
  const { principal } = useAuth();
  if (!principal) return false;
  // Dev-auth bypass: no membership rows at all → don't hide admin actions locally.
  if (principal.memberships.length === 0) return true;
  if (principal.user.isTenantAdmin) return true;
  const membership = principal.memberships.find((m) => m.tenantId === principal.activeTenantId);
  return membership?.role === WorkspaceRole.WORKSPACE_ADMIN;
}

/**
 * Whether this principal may change the brand settings of the workspace `tenantId` (Brand Workspace v2,
 * E20): an install-wide Tenant Admin, or a WORKSPACE_ADMIN of THAT workspace. The membership is looked up
 * by the workspace being edited, never the first or the active one, so a Member of this workspace who
 * administers another gets false. An approval-portal guest row grants nothing. The dev-auth principal,
 * with no membership rows at all, gets true, as in useIsWorkspaceAdmin. No principal, or no workspace,
 * is false. The API decides regardless (the same rule: apps/api/src/brand-profile/brand-access.ts).
 */
export function canEditWorkspace(principal: AuthPrincipal | null | undefined, tenantId: string | null | undefined): boolean {
  if (!principal || !tenantId) return false;
  if (principal.memberships.length === 0) return true;
  if (principal.user.isTenantAdmin) return true;
  return principal.memberships.some(
    (m) =>
      m.tenantId === tenantId &&
      m.role === WorkspaceRole.WORKSPACE_ADMIN &&
      !(Array.isArray(m.roles) && m.roles.includes(Role.STAKEHOLDER)),
  );
}

/**
 * canEditWorkspace for the signed-in user: the one client rule every Brand Workspace v2 page uses for
 * its edit controls (the wizard, the Edit pages, and the Assets and Workflow sections through their
 * `canEdit` props). useIsWorkspaceAdmin stays as it is for the pages that already use it.
 */
export function useCanEditWorkspace(tenantId: string | null | undefined): boolean {
  const { principal } = useAuth();
  return canEditWorkspace(principal, tenantId);
}

/**
 * The install-wide Tenant Admin flag only — gates cross-workspace member adds and the
 * per-member "Tenant Admin" grant/revoke toggle. Strict (no dev bypass); the API also
 * enforces it and guards the last Tenant Admin.
 */
export function useIsTenantAdmin(): boolean {
  const { principal } = useAuth();
  return principal?.user.isTenantAdmin ?? false;
}
